import NewspaperIcon from '@mui/icons-material/Newspaper';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { Box, Card, CardContent, Container, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import anthropicImage from '../../assets/landing/img/anthropic.png';
import { landingTheme } from './theme';

interface NewsArticle {
  title: string;
  excerpt: string;
  image: string;
  link: string;
  icon?: React.ReactNode;
  date: string;
}

const newsArticles: NewsArticle[] = [
  {
    title: 'Claude for Nonprofits',
    excerpt:
      'Learn how Grant Guardian leverages Claude AI to transform financial due diligence.',
    image: anthropicImage,
    link: 'https://www.anthropic.com/news/claude-for-nonprofits',
    icon: <SmartToyIcon />,
    date: 'December 2025',
  },
  {
    title: 'How Foundations Are Using AI to Vet Grantees',
    excerpt:
      'Discover how foundations are using AI to modernize their financial assessments.',
    image: '',
    link: 'https://www.philanthropy.com/solutions/how-foundations-are-using-ai-to-vet-grantees/',
    icon: <NewspaperIcon />,
    date: 'June 2025',
  },
  {
    title:
      'Foundations build AI foundations: How the Patrick J. McGovern Foundation develops AI tech',
    excerpt:
      "Learn the story behind the development of Grant Guardian from PJMF's Vice President of Strategy and Innovation, Nick Cain.",
    image: '',
    link: 'https://www.youtube.com/watch?v=FmpFjRwPJ5Y',
    icon: <YouTubeIcon />,
    date: 'February 2025',
  },
];

export default function News() {
  const [featuredArticle, ...sideArticles] = newsArticles;

  return (
    <Box
      id="news"
      sx={{
        py: 10,
        backgroundColor: `${landingTheme.colors.primary}0D`, // 0D is ~5% opacity in hex
      }}
    >
      <Container maxWidth="lg">
        {/* Section Title */}
        <Typography
          variant="h3"
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            mb: 6,
            fontSize: { xs: '1.75rem', md: '2.25rem' },
          }}
        >
          In the News
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 3,
          }}
        >
          {/* Featured Article - Left Side */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Card
              component="a"
              href={featuredArticle.link}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                textDecoration: 'none',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
            >
              {/* Featured Image */}
              <Box
                sx={{
                  height: 300,
                  backgroundImage: `url(${featuredArticle.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  position: 'relative',
                }}
              />

              <CardContent sx={{ flexGrow: 1, pt: 3 }}>
                {/* Date */}
                <Typography
                  variant="caption"
                  sx={{
                    color: landingTheme.colors.primary,
                    fontWeight: 600,
                    mb: 1,
                    display: 'block',
                  }}
                >
                  {featuredArticle.date}
                </Typography>

                {/* Title */}
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    color: 'text.primary',
                  }}
                >
                  {featuredArticle.title}
                </Typography>

                {/* Excerpt */}
                <Typography variant="body2" color="text.secondary">
                  {featuredArticle.excerpt}
                </Typography>
              </CardContent>
            </Card>
          </motion.div>

          {/* Side Articles - Right Side */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {sideArticles.map((article, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card
                  component="a"
                  href={article.link}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    textDecoration: 'none',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    position: 'relative',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 3,
                    },
                  }}
                >
                  {/* Icon */}
                  {article.icon && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        p: 2,
                        pl: 3,
                        color: landingTheme.colors.primary,
                        fontSize: '2.5rem',
                      }}
                    >
                      {article.icon}
                    </Box>
                  )}

                  <CardContent sx={{ flexGrow: 1, position: 'relative' }}>
                    {/* Date */}
                    <Typography
                      variant="caption"
                      sx={{
                        color: landingTheme.colors.primary,
                        fontWeight: 600,
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      {article.date}
                    </Typography>

                    {/* Title */}
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: '1rem',
                        color: 'text.primary',
                      }}
                    >
                      {article.title}
                    </Typography>

                    {/* Excerpt */}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: '0.875rem' }}
                    >
                      {article.excerpt}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
